import { z } from 'zod';
import { patchOf } from './zod-setup';
import { amountSchema, dateOnlySchema } from './common';
import { colourSchema } from './classification';
/**
 * Projects: which job the money was for.
 *
 * A record has AT MOST ONE project, and that is what makes a project report a
 * real P&L: sum every project, add the "not on a project" row, and the total is
 * the business's. Nothing counted twice, nothing missing. A tag cannot do that
 * and a category answers a different question - what KIND of money it is, not
 * what it was FOR.
 */
export const PROJECT_STATUSES = ['active', 'on_hold', 'completed', 'cancelled'];
export const PROJECT_BILLING_METHODS = ['fixed', 'project_hours', 'task_hours', 'staff_hours'];
const projectFields = {
    name: z.string().trim().min(1, 'Give the project a name').max(200),
    description: z.string().trim().max(4000).nullable().optional(),
    /**
     * NULL means INTERNAL - an office renovation, an unbilled product build.
     * Such a project has costs and no customer, and forcing a fake one poisons
     * the customer list. See `projects.customerId` in schema.ts.
     */
    customerId: z.string().trim().max(36).nullable().optional(),
    billingMethod: z.enum(PROJECT_BILLING_METHODS).optional(),
    rate: amountSchema.optional(),
    budgetAmount: amountSchema.optional(),
    budgetHours: z.number().min(0).max(1_000_000).optional(),
    status: z.enum(PROJECT_STATUSES).optional(),
    startDate: dateOnlySchema.nullable().optional(),
    endDate: dateOnlySchema.nullable().optional(),
    colour: colourSchema.optional(),
};
/**
 * An internal project bills nobody, so an hourly billing method on one is a
 * setting with no effect that reads on screen as a promise. Refused in the
 * schema rather than in the service, so an API-key caller and the SPA get the
 * same answer.
 */
const internalIsFixed = (v, ctx) => {
    if (v.customerId == null && v.billingMethod && v.billingMethod !== 'fixed') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['billingMethod'],
            message: 'An internal project has no customer to bill, so it cannot be charged by the hour. ' +
                'Pick a customer, or leave the billing method as a fixed amount.',
        });
    }
};
export const createProjectSchema = z.object(projectFields).strict().superRefine(internalIsFixed);
/**
 * Built from the field object rather than from `createProjectSchema`, so the
 * refinement is added once, after the fields are made optional. `patchOf`
 * rather than `.partial()`: a field that was not sent must stay absent, never
 * take its create-time default (zod 4 - see zod-setup.ts).
 */
export const updateProjectSchema = patchOf(z.object(projectFields)).strict().superRefine(internalIsFixed);
export const listProjectsQuerySchema = z
    .object({
    q: z.string().trim().max(200).optional(),
    status: z.enum([...PROJECT_STATUSES, 'all']).default('all'),
    customerId: z.string().trim().max(36).optional(),
    /**
     * The window the money figures cover. Absent means "everything", which is
     * the right default for a picker that has to show a project that has not
     * been touched this quarter.
     */
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    /**
     * A capped slab, not a cursor: projects are bounded by what a business
     * actually runs, like bank accounts.
     */
    limit: z.coerce.number().int().min(1).max(500).default(500),
})
    .strict();
export const projectSummaryQuerySchema = z
    .object({
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
})
    .strict();
//# sourceMappingURL=projects.js.map